import { ref, computed, watch } from 'vue';
import { t } from '@/utils/i18n-helper';
import canteraService from '@/services/perfil/cantera';
import { usePlazosSelection } from '@/composables/front/perfil/usePlazosSelection';
import { useSearchDebounce } from '@/composables/front/common/useSearchDebounce';
import { useListasAbonados } from './useListasAbonados';

export function useCantera() {
    const deportistas = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const { search, debouncedSearch } = useSearchDebounce({ delay: 600, minChars: 1 });

    /** Reset pagination and re-fetch when search changes (debounced). */
    watch(debouncedSearch, () => {
        fetchDeportistas(1, perPage.value);
    });

    // Fuente de verdad única para paginación — mismo patrón que useAbonos.
    const meta = ref({ current_page: 1, last_page: 1, total: 0, per_page: 10 });

    const page    = computed(() => meta.value.current_page);
    const perPage = computed(() => meta.value.per_page);
    const isEmpty = computed(() => !loading.value && deportistas.value.length === 0);

    let fetchGeneration = 0;

    // ── Listas de abonados filter ──
    const {
        listas,
        listasLoading,
        selectedListaId,
        onListaFilterChange,
    } = useListasAbonados({
        onFilterChange: () => { fetchDeportistas(1, perPage.value); },
    });

    const detalleCache      = ref(/** @type {Map<number|string, Object>} */ (new Map()));
    const detalleLoadingIds = ref(/** @type {Set<number|string>} */ (new Set()));
    const detalleErrorCache = ref(/** @type {Map<number|string, string>} */ (new Map()));

    /**
     * Carga el listado de deportistas (paginado).
     * @param {number} newPage
     * @param {number} newPerPage
     */
    const fetchDeportistas = async(newPage = page.value, newPerPage = perPage.value) => {
        const generation = ++fetchGeneration;
        loading.value = true;
        error.value   = null;

        try {
            const result = await canteraService.getDeportistas({
                page:     newPage,
                per_page: newPerPage,
                filters:  {
                    search: search.value,
                    lista_id: selectedListaId.value,
                },
            });

            if (generation !== fetchGeneration) return;

            deportistas.value = result.items;
            meta.value        = result.meta;
        } catch (err) {
            if (generation !== fetchGeneration) return;

            error.value = err?.message ?? t('perfil.error_cargar_cantera', {}, 'Error al cargar la cantera');
            deportistas.value = [];
            meta.value = { current_page: 1, last_page: 1, total: 0, per_page: newPerPage };

            if (process.env.NODE_ENV !== 'production') {
                console.error('[useCantera] Error al obtener deportistas:', err);
            }
        } finally {
            if (generation === fetchGeneration) {
                loading.value = false;
            }
        }
    };

    /**
     * Carga el detalle de un deportista (plazos + url_recibo).
     * Usa caché en memoria para evitar peticiones duplicadas.
     * @param {number|string} deportistaId
     */
    const loadDetalle = async deportistaId => {
        if (detalleCache.value.has(deportistaId)) return;
        if (detalleLoadingIds.value.has(deportistaId)) return;

        const nextLoading = new Set(detalleLoadingIds.value);
        nextLoading.add(deportistaId);
        detalleLoadingIds.value = nextLoading;

        if (detalleErrorCache.value.has(deportistaId)) {
            const nextErrors = new Map(detalleErrorCache.value);
            nextErrors.delete(deportistaId);
            detalleErrorCache.value = nextErrors;
        }

        try {
            const deportista = deportistas.value.find(item => item.id === deportistaId) ?? null;
            const plazos = await canteraService.getDeportistaDetalle(deportistaId);
            const nextCache = new Map(detalleCache.value);
            nextCache.set(deportistaId, {
                plazos,
                url_recibo: canteraService.buildReciboUrl(deportista?.url_recibo ?? ''),
            });
            detalleCache.value = nextCache;
        } catch (err) {
            const nextErrors = new Map(detalleErrorCache.value);
            nextErrors.set(
                deportistaId,
                err?.message ?? t('perfil.error_cargar_plazos', {}, 'Error al cargar los plazos')
            );
            detalleErrorCache.value = nextErrors;

            if (process.env.NODE_ENV !== 'production') {
                console.error(`[useCantera] Error al obtener detalle del deportista ${deportistaId}:`, err);
            }
        } finally {
            const nextLoading2 = new Set(detalleLoadingIds.value);
            nextLoading2.delete(deportistaId);
            detalleLoadingIds.value = nextLoading2;
        }
    };

    /**
     * Invalida la entrada de caché de un deportista y recarga su detalle.
     *
     * Encapsula la mutación de detalleCache — usePlazosSelection no debe
     * saber que la caché es un Map.
     *
     * @param {number|string} itemId
     */
    const invalidateAndReload = async itemId => {
        const nextCache = new Map(detalleCache.value);
        nextCache.delete(itemId);
        detalleCache.value = nextCache;

        await loadDetalle(itemId);
    };

    const {
        selectedPlazos,
        isPagable,
        togglePlazo,
        isSelected,
        selectedCount,
        totalSeleccionado,
        clearSelection,
        pagoLoading,
        pagoError,
        pagarPlazos,
        pagarYRefrescarPlazos,
        refreshDetalle,
    } = usePlazosSelection({
        items: deportistas,
        detalleCache,
        buildPaymentPayload: (plazoIds, extra = {}) => ({
            plazos: plazoIds,
            tipo_venta: 'plazosBase',
            filters: {},
            ...(extra.jetToken ? { jetToken: extra.jetToken } : {}),
            ...(extra.guardarTarjeta ? { guardarTarjeta: true } : {}),
        }),
        onRefreshDetail: invalidateAndReload,
        processPayment: canteraService.procesarPagoPlazos,
    });

    return {
        deportistas,
        loading,
        error,
        search,
        meta,
        page,
        perPage,
        isEmpty,
        fetchDeportistas,
        detalleCache,
        detalleLoadingIds,
        detalleErrorCache,
        loadDetalle,
        selectedPlazos,
        isPagable,
        togglePlazo,
        isSelected,
        selectedCount,
        totalSeleccionado,
        clearSelection,
        pagoLoading,
        pagoError,
        pagarPlazos,
        pagarYRefrescarPlazos,
        refreshDetalle,
        invalidateAndReload,
        // listas de abonados filter
        listas,
        listasLoading,
        selectedListaId,
        onListaFilterChange,
    };
}
